import { useState, useEffect } from 'react';

// Editable contact line (name + email + phone) for the application detail
// header. Display mode renders mailto/tel links; edit mode opens an inline
// form that PUTs to /api/admin/applications/:id (E4). Anybody with
// view_admin_panel can use it — server enforces the cross-manager
// visibility gate.
export function ContactRow({ application, onSave }) {
  const a = application;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    first_name: a.first_name || '',
    last_name: a.last_name || '',
    preferred_name: a.preferred_name || '',
    email: a.email || '',
    phone: a.phone || '',
  });
  useEffect(() => {
    if (!editing) {
      setForm({
        first_name: a.first_name || '',
        last_name: a.last_name || '',
        preferred_name: a.preferred_name || '',
        email: a.email || '',
        phone: a.phone || '',
      });
      setErr('');
    }
  }, [a.id, editing]);

  async function save() {
    // Send only the fields that actually changed — fewer fields to audit-log
    // and clearer intent in the activity stream.
    const patch = {};
    for (const k of ['first_name','last_name','preferred_name','email','phone']) {
      if ((form[k] || '') !== (a[k] || '')) patch[k] = form[k];
    }
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    setBusy(true); setErr('');
    try {
      await onSave(patch);
      setEditing(false);
    } catch (e) {
      setErr(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <div style={{ fontSize: 13, color: '#666', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <a href={`mailto:${a.email}`} style={{ color: 'var(--forest)' }}>{a.email}</a>
        <span>·</span>
        <a href={`tel:${a.phone}`} style={{ color: 'var(--forest)' }}>{a.phone}</a>
        {onSave && (
          <button onClick={() => setEditing(true)}
            style={{ background: 'none', border: '1px solid #d0d4d9', borderRadius: 4,
                     padding: '1px 8px', fontSize: 11, color: '#555', cursor: 'pointer' }}>
            Edit
          </button>
        )}
      </div>
    );
  }
  // Inline inputs (NOT a sub-component) — defining a Field component
  // inside ContactRow makes React remount the input on every keystroke
  // because the function identity changes each render, killing focus
  // after one character. Lesson learned.
  const fieldStyle = { padding: '4px 6px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 4 };
  const labelStyle = { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#555', minWidth: 140 };
  return (
    <div style={{ marginTop: 6, padding: 10, background: '#fafbfc', border: '1px solid #e5e8eb', borderRadius: 6 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={labelStyle}>First name
          <input value={form.first_name}
            onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
            style={fieldStyle} />
        </label>
        <label style={labelStyle}>Last name
          <input value={form.last_name}
            onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            style={fieldStyle} />
        </label>
        <label style={labelStyle}>Preferred (optional)
          <input value={form.preferred_name} placeholder="leave blank to clear"
            onChange={(e) => setForm((f) => ({ ...f, preferred_name: e.target.value }))}
            style={fieldStyle} />
        </label>
        <label style={labelStyle}>Email
          <input type="email" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            style={fieldStyle} />
        </label>
        <label style={labelStyle}>Phone
          <input type="tel" value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            style={fieldStyle} />
        </label>
      </div>
      {err && <div style={{ fontSize: 11, color: '#c62828', marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={save} disabled={busy}
          style={{ padding: '4px 12px', fontSize: 12, background: 'var(--forest)', color: '#fff',
                   border: 'none', borderRadius: 4, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={() => setEditing(false)} disabled={busy}
          style={{ padding: '4px 12px', fontSize: 12, background: '#fff', color: '#555',
                   border: '1px solid #d0d4d9', borderRadius: 4, cursor: busy ? 'wait' : 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
